/**
 * Image Upload Service
 * Handles uploading test images to S3 using pre-signed URLs
 */

import { base64ToBlob } from 'utils/base64ToBlob';
import http from './http/http';
import { Methods } from 'types/api.d';
import { TestType } from 'types/testType';
import { IMAGE_STACK_SIZE, getPipelineVersion, getModelMetadataFromStore, getModelMetaDataTags } from './models';
import type { ModelMetadata, S3MetadataJson, SelfReportedResult } from 'types/models';
import { detectHmo } from 'utils/hmoDetection';
import type { HMOType } from 'constants/hmo';
import { ResultStatus } from 'types/imageRecognition';
import { sentryLogException, sentryLogMessage } from './sentryLogger';
import { SENTRY_EVENT_TYPES } from 'constants/sentryEvents';
import { formatImageUrlsForSentry } from 'utils/sentryHelpers';

const MAX_RETRIES = 3;
const RETRY_DELAY_BASE = 1000; // 1 second base delay
const UTC_OFFSET_HOURS = parseInt(import.meta.env.VITE_UTC_OFFSET_HOURS || '2', 10);
const UTC_OFFSET_MS = UTC_OFFSET_HOURS * 60 * 60 * 1000;

interface PresignedUrl {
  imageIndex?: number;
  url: string;
  expiresIn: number;
}

interface DownloadUrl {
  imageIndex: number;
  url: string;
  expiresIn: number;
}

interface PresignedUrlResponse {
  presignedUrls: Array<PresignedUrl>;
  downloadUrls: Array<DownloadUrl>;
  metadataUrl?: PresignedUrl;
  uploadPath: string;
}

// --- ZIP-related interfaces (commented out — replaced by per-image download URLs) ---
// interface GenerateZipUrl {
//   url: string;
//   expiresIn: number;
// }
//
// interface ZipCreationResponse {
//   zipDownloadUrl: {
//     url: string;
//     expiresIn: number;
//   };
//   maxQualityImageUrl?: {
//     url: string;
//     expiresIn: number;
//   };
// }
// --- End ZIP-related interfaces ---

export interface UploadResult {
  success: boolean;
  uploadPath: string;
  imageDownloadUrls?: Array<string>;
  // --- Commented out — replaced by per-image download URLs ---
  // zipDownloadUrl?: string;
  // maxQualityImageUrl?: string;
}

interface UploadMetadata {
  timestamp?: number;
  testResult?: string;
  hmo?: HMOType;
  modelMetadata?: ModelMetadata;
  imageCaptureTimestamp?: number;
  sessionId?: string;
}

interface ImageData {
  index: number;
  base64: string;
}

/**
 * Request pre-signed URLs from Lambda
 */
async function requestPresignedUrls(
  metadata: UploadMetadata, 
  testResult: ResultStatus,
  testType?: string | null,
  sessionId?: string | null
): Promise<PresignedUrlResponse> {
  const lambdaEndpoint = import.meta.env.VITE_LAMBDA_API_ENDPOINT;
  
  if (!lambdaEndpoint) {
    throw new Error(
      'VITE_LAMBDA_API_ENDPOINT is not configured. ' +
      'Please set this environment variable to your Lambda function URL or API Gateway endpoint.'
    );
  }
  
  const authKey = import.meta.env.VITE_AUTH_KEY;

  if (!authKey) {
    throw new Error('VITE_AUTH_KEY is not configured');
  }

  try {
    const timestamp = Date.now() + UTC_OFFSET_MS;
    const requestData: any = {
      metadata: {
        ...metadata,
        testResult: testResult ? testResult.toString() : 'false',
        timestamp: timestamp,
        imagesStackSize: IMAGE_STACK_SIZE,
        ...(testType && { testType }),
        ...(sessionId && { sessionId }),
      },
    };
    const responseData = await http.request<PresignedUrlResponse>({
      key: 'requestPresignedUrls',
      data: requestData,
      extraHeader: {
        'Content-Type': 'application/json',
        'X-API-Key': authKey,
      },
    });
    
    
    return responseData;
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || 'Unknown error';
    const statusCode = error?.statusCode || error?.response?.status || 'Unknown';
    sentryLogException({
      error,
      tags: {
        component: 'imageUpload',
        category: 'presignedUrls',
      },
      extra: {
        statusCode,
        errorMessage,
        imageCount: IMAGE_STACK_SIZE,
        timestamp: new Date().toISOString(),
      },
    });
    throw new Error(
      `Failed to get pre-signed URLs: ${statusCode} ${errorMessage}`
    );
  }
}

// --- requestZipCreation function (commented out — replaced by per-image download URLs) ---
// /**
//  * Request ZIP creation from Lambda
//  * @param uploadPath - The S3 path where images are stored
//  * @returns Promise with ZIP and max quality image download URLs
//  */
// async function requestZipCreation(
//   uploadPath: string,
//   maxQualityImageIndex?: number
// ): Promise<{ zipDownloadUrl?: string; maxQualityImageUrl?: string }> {
//   const lambdaEndpoint = import.meta.env.VITE_LAMBDA_API_ENDPOINT;
//   if (!lambdaEndpoint) {
//     throw new Error('VITE_LAMBDA_API_ENDPOINT is not configured.');
//   }
//   const authKey = import.meta.env.VITE_AUTH_KEY;
//   if (!authKey) {
//     throw new Error('VITE_AUTH_KEY is not configured');
//   }
//   try {
//     const requestData = {
//       uploadPath,
//       imagesStackSize: IMAGE_STACK_SIZE,
//       ...(typeof maxQualityImageIndex === 'number' ? { maxQualityImageIndex } : {}),
//     };
//     const responseData = await http.request<ZipCreationResponse>({
//       key: 'createZip',
//       data: requestData,
//       extraHeader: { 'Content-Type': 'application/json', 'X-API-Key': authKey },
//     });
//     return {
//       zipDownloadUrl: responseData.zipDownloadUrl?.url,
//       maxQualityImageUrl: responseData.maxQualityImageUrl?.url,
//     };
//   } catch (error: any) {
//     const errorMessage = error?.data?.message || error?.message || 'Unknown error';
//     const statusCode = error?.statusCode || error?.response?.status || 'Unknown';
//     sentryLogException({
//       error,
//       tags: { component: 'imageUpload', category: 'zipCreation' },
//       extra: { statusCode, errorMessage, uploadPath, timestamp: new Date().toISOString() },
//     });
//     throw new Error(`Failed to create ZIP file: ${statusCode} ${errorMessage}`);
//   }
// }
// --- End requestZipCreation ---

/**
 * Validates pre-signed URL expiration before attempting upload
 */
function validateUrlExpiration(url: string): void {
  try {
    const urlObj = new URL(url);
    const expirationDate = urlObj.searchParams.get('X-Amz-Date');
    const expiresIn = urlObj.searchParams.get('X-Amz-Expires');
    
    if (expirationDate && expiresIn) {
      const expirationTime = new Date(expirationDate).getTime() + (parseInt(expiresIn) * 1000);
      
      if (Date.now() >= expirationTime) {
        throw new Error('Pre-signed URL has expired. Please request new URLs.');
      }
    }
  } catch (error: any) {
    if (error.message?.includes('expired')) {
      sentryLogException({
        error,
        tags: {
          component: 'imageUpload',
          category: 'urlValidation',
        },
        extra: {
          errorType: 'expired_url',
          timestamp: new Date().toISOString(),
        },
      });
      throw error; 
    }
  }
}

/**
 * Parses S3 XML error response and returns a formatted Error
 */
function parseS3Error(response: any): Error {
  const responseText = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);

  if (responseText.includes('<Error>')) {
    const errorCode = responseText.match(/<Code>(.*?)<\/Code>/)?.[1];
    const errorMessage = responseText.match(/<Message>(.*?)<\/Message>/)?.[1];
    
    // Handle specific S3 error codes
    switch (errorCode) {
      case 'SignatureDoesNotMatch':
        return new Error(
          `Pre-signed URL signature invalid or expired. ` +
          `URL may be too old or was modified. ${errorMessage || ''}`
        );
        
      case 'AccessDenied':
        return new Error(
          'Access denied. Check S3 bucket permissions and CORS configuration. ' +
          `Details: ${errorMessage || 'Insufficient permissions'}`
        );
        
      case 'TokenRefreshRequired':
        return new Error(
          'AWS credentials expired. Please refresh and try again. ' +
          `Details: ${errorMessage || 'Temporary credentials need refresh'}`
        );
        
      case 'RequestTimeout':
        return new Error(`Upload timeout: ${errorMessage || 'Network may be slow or unstable'}`);
        
      case 'SlowDown':
        return new Error(`S3 throttling detected: ${errorMessage || 'Too many requests, retrying...'}`);
        
      default:
        return new Error(
          `S3 upload failed: ${errorCode || 'Unknown error'} - ${errorMessage || responseText.substring(0, 200)}`
        );
    }
  }

  return new Error(`Upload failed: ${response.status} ${response.statusText || ''} - ${responseText.substring(0, 200)}`);
}

/**
 * Calculates exponential backoff delay for retries
 */
function calculateBackoffDelay(attempt: number): number {
  return RETRY_DELAY_BASE * Math.pow(2, attempt - 1);
}

/**
 * File type for upload
 */
type FileType = 'image' | 'json';

/**
 * Sentry tag value for which S3 object failed (`image_${index}` or `metadata.json`).
 */
const getS3UploadFileTag = (fileType: FileType, identifier?: string | number): string => {
  if (fileType === 'image' && typeof identifier === 'number') {
    return `image_${identifier}`;
  }
  return 'metadata.json';
};

/**
 * Upload configuration
 */
interface UploadConfig {
  url: string;
  blob: Blob;
  fileType: FileType;
  identifier?: string | number; // For logging (imageIndex or 'metadata')
}

/**
 * Generic function to upload any file type to S3 with retry logic
 */
async function uploadFileToS3(
  config: UploadConfig,
  maxRetries: number = MAX_RETRIES
): Promise<void> {
  const { url, blob, fileType, identifier } = config;
  
  // Validate URL expiration before attempting upload
  validateUrlExpiration(url);

  const contentType = fileType === 'image' ? 'image' : 'application/json';
  const logPrefix = fileType === 'image' 
    ? `Image ${identifier}` 
    : 'Model metadata JSON';

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Upload with PUT method - only include Content-Type header
      // DO NOT add Authorization or other headers - pre-signed URLs are header-sensitive
      await http.request({
        key: 'uploadImageToS3',
        otherApiConfig: {
          url: url,
          method: Methods.PUT,
          noAuthHeader: true,
        },
        data: blob,
        extraHeader: {
          'Content-Type': contentType,
        },
      });

      console.log(`${logPrefix} uploaded successfully (attempt ${attempt})`);
      return;
    } catch (error: any) {
      const response = error.response;

      // Handle network errors or other non-HTTP errors
      if (!response) {
        const networkError = error instanceof Error ? error : new Error(error?.message || 'Network error');
        
        if (attempt === maxRetries) {
          console.error(`${logPrefix} upload failed after ${maxRetries} attempts:`, networkError);
          sentryLogException({
            error: networkError,
            tags: {
              component: 'imageUpload',
              category: 's3Upload',
              errorType: 'network',
              errorEventType: 's3ImageUpload',
              file: getS3UploadFileTag(fileType, identifier),
              errorCode: error?.statusCode || 'unknown',
            },
            extra: {
              fileType,
              image: `Image ${identifier}`,
              maxRetries,
              attempt,
              errorData: error,
              timestamp: new Date().toISOString(),
            },
          });
          throw networkError;
        }

        const delay = calculateBackoffDelay(attempt);
        console.warn(`${logPrefix} upload attempt ${attempt} failed, retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      const s3Error = parseS3Error(response);

      if (attempt === maxRetries) {
        console.error(`${logPrefix} upload failed after ${maxRetries} attempts:`, s3Error);
        sentryLogException({
          error: s3Error,
          tags: {
            component: 'imageUpload',
            category: 's3Upload',
            errorType: 's3',
            errorEventType: 's3ImageUpload',
            file: getS3UploadFileTag(fileType, identifier),
          },
          extra: {
            fileType,
            image: `Image ${identifier}`,
            maxRetries,
            attempt,
            responseStatus: response?.status,
            timestamp: new Date().toISOString(),
          },
        });
        throw s3Error;
      }

      const delay = calculateBackoffDelay(attempt);
      console.warn(`${logPrefix} upload attempt ${attempt} failed, retrying in ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

/**
 * Prepares image blob from base64 data
 */
function prepareImageBlob(base64: string): Blob {
  return base64ToBlob(base64);
}

/**
 * Prepares JSON blob from complete S3 metadata
 * @param metadata - Complete S3 metadata JSON structure
 */
function prepareJsonBlob(metadata: S3MetadataJson): Blob {
  const jsonString = JSON.stringify(metadata, null, 2);
  return new Blob([jsonString], { type: 'application/json' });
}

interface BuildS3MetadataParams {
  testResult: ResultStatus;
  sessionId?: string | null;
  uploadMetadata: UploadMetadata;
  pipelineVersion?: Awaited<ReturnType<typeof getPipelineVersion>> | null;
  selfReportedResult?: SelfReportedResult | null;
}

/**
 * Build complete S3 metadata JSON (shared by full upload and metadata-only update)
 */
const buildS3Metadata = ({
  testResult,
  sessionId,
  uploadMetadata,
  pipelineVersion,
  selfReportedResult,
}: BuildS3MetadataParams): S3MetadataJson => {
  const testResultTimestamp = Date.now() + UTC_OFFSET_MS;
  return {
    imageCaptureTimestamp: uploadMetadata.imageCaptureTimestamp || testResultTimestamp,
    testResultTimestamp,
    sessionId: sessionId || null,
    testResult,
    modelMetadata: uploadMetadata.modelMetadata!,
    ...(selfReportedResult && {
      selfReportedResult: selfReportedResult,
    }),
    ...(pipelineVersion && {
      pipelineVersion,
    }),
  };
};

interface MetadataUpdateUrlResponse {
  metadataUrl: PresignedUrl;
  uploadPath: string;
}

/**
 * Request a pre-signed PUT URL for metadata.json under an existing uploadPath
 */
async function requestMetadataUpdateUrl(
  uploadPath: string
): Promise<MetadataUpdateUrlResponse> {
  const lambdaEndpoint = import.meta.env.VITE_LAMBDA_API_ENDPOINT;

  if (!lambdaEndpoint) {
    throw new Error(
      'VITE_LAMBDA_API_ENDPOINT is not configured. ' +
      'Please set this environment variable to your Lambda function URL or API Gateway endpoint.'
    );
  }

  const authKey = import.meta.env.VITE_AUTH_KEY;

  if (!authKey) {
    throw new Error('VITE_AUTH_KEY is not configured');
  }

  try {
    const responseData = await http.request<MetadataUpdateUrlResponse>({
      key: 'updateMetadata',
      data: {
        action: 'updateMetadata',
        uploadPath,
      },
      extraHeader: {
        'Content-Type': 'application/json',
        'X-API-Key': authKey,
      },
    });

    return responseData;
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || 'Unknown error';
    const statusCode = error?.statusCode || error?.response?.status || 'Unknown';
    sentryLogException({
      error,
      tags: {
        component: 'imageUpload',
        category: 'metadataUpdateUrl',
      },
      extra: {
        statusCode,
        errorMessage,
        uploadPath,
        timestamp: new Date().toISOString(),
      },
    });
    throw new Error(
      `Failed to get metadata update URL: ${statusCode} ${errorMessage}`
    );
  }
}

/**
 * Update metadata.json under an existing S3 uploadPath (adds selfReportedResult).
 * Does not upload images or create a new folder.
 */
export async function updateMetadataJson(
  uploadPath: string,
  testResult: ResultStatus,
  metadata?: UploadMetadata,
  sessionId?: string | null,
  selfReportedResult?: SelfReportedResult | null,
): Promise<void> {
  try {
    const [pipelineVersion, modelMetadata] = await Promise.all([
      getPipelineVersion(),
      Promise.resolve(getModelMetadataFromStore()),
    ]);

    const uploadMetadata: UploadMetadata = {
      ...metadata,
      modelMetadata: modelMetadata || metadata?.modelMetadata,
    };

    if (!uploadMetadata.modelMetadata) {
      throw new Error('No model metadata available to upload');
    }

    const { metadataUrl } = await requestMetadataUpdateUrl(uploadPath);

    if (!metadataUrl?.url) {
      throw new Error('No metadata URL provided by Lambda for update');
    }

    const s3Metadata = buildS3Metadata({
      testResult,
      sessionId,
      uploadMetadata,
      pipelineVersion,
      selfReportedResult,
    });

    await uploadFileToS3({
      url: metadataUrl.url,
      blob: prepareJsonBlob(s3Metadata),
      fileType: 'json',
      identifier: 'metadata',
    });

    console.log('Metadata JSON updated successfully for', uploadPath);
  } catch (error) {
    console.error('Failed to update metadata JSON:', error);
    sentryLogException({
      error,
      tags: {
        component: 'imageUpload',
        category: 'updateMetadataJson',
      },
      extra: {
        uploadPath,
        testResult,
        sessionId,
        selfReportedResult,
        timestamp: new Date().toISOString(),
      },
    });
    throw error;
  }
}

interface EagerUploadArgs {
  images: ImageData[];
  testResult: ResultStatus;
  metadata?: UploadMetadata;
  testType?: TestType | null;
  sessionId?: string | null;
  maxQualityImageIndex?: number;
}

/** In-flight / completed eager uploads keyed by session (idempotent across remounts) */
const eagerUploadPromises = new Map<string, Promise<UploadResult>>();

/**
 * Start or reuse a single eager upload per session key.
 * Clears the cache entry on failure so submit/fallback can retry.
 */
export const getOrStartEagerUpload = (
  key: string,
  args: EagerUploadArgs
): Promise<UploadResult> => {
  const existing = eagerUploadPromises.get(key);
  if (existing !== undefined) {
    return existing;
  }

  const uploadPromise = uploadTestImages(
    args.images,
    args.testResult,
    args.metadata,
    args.testType,
    args.sessionId,
    null,
    args.maxQualityImageIndex,
    false,
  ).catch((error) => {
    eagerUploadPromises.delete(key);
    throw error;
  });

  eagerUploadPromises.set(key, uploadPromise);
  return uploadPromise;
};

/**
 * Upload all test images and model metadata JSON to S3
 * @param images - Array of image data with index and base64
 * @param testResult - Test result (true = POSITIVE, false = NEGATIVE)
 * @param metadata - Upload metadata (modelMetadata and pipelineVersion are fetched internally)
 * @param testType - Optional test type from URL parameter
 * @param sessionId - Optional session ID from URL parameter (only used in JSON metadata, not S3 path)
 * @param selfReportedResult - Optional self-reported result for trial HMO (true = POSITIVE, false = NEGATIVE)
 */
export async function uploadTestImages(
  images: ImageData[],
  testResult: ResultStatus,
  metadata?: UploadMetadata,
  testType?: TestType | null,
  sessionId?: string | null,
  selfReportedResult?: SelfReportedResult | null,
  maxQualityImageIndex?: number,
  shouldUploadToSentry: boolean = false,
): Promise<UploadResult> {
  try {
    // Fetch pipelineVersion and modelMetadata internally
    const [pipelineVersion, modelMetadata] = await Promise.all([
      getPipelineVersion(),
      Promise.resolve(getModelMetadataFromStore()),
    ]);

    const uploadMetadata: UploadMetadata = {
      ...metadata,
      modelMetadata: modelMetadata || metadata?.modelMetadata, // Use fetched or fallback to passed value
      timestamp: Date.now() + UTC_OFFSET_MS,
      testResult: testResult.toString(),
      hmo: detectHmo(),
      ...(sessionId && { sessionId }),
    };

    const testTypeString = testType ? String(testType) : TestType.FOB;
    const { presignedUrls, downloadUrls, metadataUrl, uploadPath } = await requestPresignedUrls(
      uploadMetadata,
      testResult,
      testTypeString,
      sessionId
    );

    
    if (presignedUrls.length !== IMAGE_STACK_SIZE) {
      console.warn(
        `Expected ${IMAGE_STACK_SIZE} pre-signed URLs, got ${presignedUrls.length}`
      );
    }

    // Prepare all upload promises (images + metadata JSON); labels align by index with `results` from allSettled
    const uploadPromises: Promise<void>[] = [];
    const uploadFileLabels: Array<string> = [];

    // Upload images
    images.forEach(({ index, base64 }) => {
      const presignedUrl = presignedUrls.find((url) => url.imageIndex === index);
      if (!presignedUrl) {
        throw new Error(`No pre-signed URL found for image index ${index}`);
      }
      
      const imageBlob = prepareImageBlob(base64);
      uploadFileLabels.push(`image_${index}`);
      uploadPromises.push(
        uploadFileToS3({
          url: presignedUrl.url,
          blob: imageBlob,
          fileType: 'image',
          identifier: index,
        })
      );
    });

    // Upload metadata JSON if available
    if (metadataUrl && uploadMetadata.modelMetadata) {
      const s3Metadata = buildS3Metadata({
        testResult,
        sessionId,
        uploadMetadata,
        pipelineVersion,
        selfReportedResult,
      });
      
      const jsonBlob = prepareJsonBlob(s3Metadata);
      uploadFileLabels.push('metadata.json');
      uploadPromises.push(
        uploadFileToS3({
          url: metadataUrl.url,
          blob: jsonBlob,
          fileType: 'json',
          identifier: 'metadata',
        })
      );
      console.log('Metadata JSON upload added to queue');
      // should upload to sentry
      if (shouldUploadToSentry) {
        const formattedTimestamp = new Date().toISOString();
        const imageDownloadUrlsForSentry = downloadUrls?.map(({ url }) => url as string);
        sentryLogMessage({
          message: `Image Upload to S3`,
          level: 'info',
          tags: {
            event_type: SENTRY_EVENT_TYPES.IMAGE_UPLOAD,
            component: 'ImageUpload',
            category: 'uploaded_to_s3',
            model_result: testResult,
            ...getModelMetaDataTags(),
          },
          extra: {
            timestamp: formattedTimestamp,
            modelResult: testResult,
            ...formatImageUrlsForSentry(imageDownloadUrlsForSentry),
          },
        });
      }
    } else {
      if (!metadataUrl) {
        console.warn('No metadata URL provided by Lambda');
      }
      if (!uploadMetadata.modelMetadata) {
        console.warn('No model metadata available to upload');
      }
    }

    // Upload all files (images + JSON) in parallel
    const results = await Promise.allSettled(uploadPromises);
    
    const failures = results.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    
    if (failures.length > 0) {
      const errorMessages = failures.map((f) => f.reason?.message || f.reason);
      const failedFileLabels = results
        .map((result, i) =>
          result.status === 'rejected' ? uploadFileLabels[i] : null
        )
        .filter((label): label is string => label !== null);
      const fileTag =
        failedFileLabels.length > 0 ? failedFileLabels.join(', ') : 'unknown';

      console.error(`${failures.length} upload(s) failed:`, errorMessages);
      const uploadError = new Error(`Upload failed: ${failures.length} file(s) could not be uploaded. ${errorMessages.join('; ')}`);
      sentryLogException({
        error: uploadError,
        tags: {
          component: 'imageUpload',
          category: 'uploadBatch',
          file: fileTag,
        },
        extra: {
          failureCount: failures.length,
          totalCount: uploadPromises.length,
          errorMessages,
          failedFiles: failedFileLabels,
          testResult,
          sessionId,
          timestamp: new Date().toISOString(),
        },
      });
      throw uploadError;
    }
    
    console.log('All uploads completed successfully');

    // Map download URLs to a simpler format for consumers
    const imageDownloadUrls = downloadUrls?.map(({ url }) => (url as string));

    // --- ZIP creation logic (commented out — replaced by per-image download URLs) ---
    // let zipDownloadUrl: string | undefined;
    // let maxQualityImageUrl: string | undefined;
    //
    // // Request ZIP creation only for self-report (clinical trial) flow
    // if (selfReportedResult) {
    //   try {
    //     const zipCreationResult = await requestZipCreation(uploadPath, maxQualityImageIndex);
    //     zipDownloadUrl = zipCreationResult.zipDownloadUrl;
    //     maxQualityImageUrl = zipCreationResult.maxQualityImageUrl;
    //
    //     console.log('ZIP file created successfully');
    //     sentryLogMessage({
    //       message: 'ZIP file created successfully',
    //       level: 'info',
    //       tags: {
    //         component: 'imageUpload',
    //         category: 'zipCreation',
    //       },
    //       extra: {
    //         ...getModelMetaDataExtra(),
    //         zipDownloadUrl,
    //         maxQualityImageUrl,
    //         timestamp: new Date().toISOString(),
    //       },
    //     });
    //   } catch (error) {
    //     console.error('Failed to create ZIP file:', error);
    //   }
    // }
    // --- End ZIP creation logic ---

    return {
      success: true,
      uploadPath,
      imageDownloadUrls,
      // zipDownloadUrl,
      // maxQualityImageUrl,
    };

  } catch (error) {
    console.error('Failed to upload test images:', error);
    sentryLogException({
      error,
      tags: {
        component: 'imageUpload',
        category: 'uploadTestImages',
      },
      extra: {
        imageCount: images.length,
        testResult,
        sessionId,
        timestamp: new Date().toISOString(),
      },
    });
    throw error; // Re-throw so callers can handle upload failures
  }
}


